import { useState } from "react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { assignAppointment, unassignAppointment } from "@/services/appointment-assignment.service";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { assignmentChangesNothing, isUnassignable, mayGiveWorker } from "@/lib/appointment-assignment";
import type { WorkerAssignmentRequest } from "../components/SimpleAssignmentModal";
import { toast } from "sonner";

interface UseSubsAppointmentActionsProps {
  appointment: AppointmentJoin;
  onAppointmentUpdate?: (updatedAppointment: AppointmentJoin) => void;
  onModalClose?: () => void;
}

export function useSubsAppointmentActions({ appointment, onAppointmentUpdate, onModalClose }: UseSubsAppointmentActionsProps) {
  const [assigningIndividual, setAssigningIndividual] = useState(false);
  const showAnswer = (answer: AppointmentJoin) => onAppointmentUpdate?.({ ...appointment, ...answer });

  const handleAssignIndividual = async ({ individualId, pay }: WorkerAssignmentRequest) => {
    setAssigningIndividual(true);
    try {
      const assignment = { type: "individual" as const, resourceId: individualId, pay };
      const changesNothing = assignmentChangesNothing("subcontractor", assignment, appointment);
      showAnswer(await assignAppointment(appointment.id, assignment));
      if (changesNothing) toast.info("No changes");
      else toast.success("Individual assigned successfully");
      onModalClose?.();
    } catch (error) {
      toast.error(fieldActionMessage(error, "Failed to assign individual"));
      throw error;
    } finally {
      setAssigningIndividual(false);
    }
  };

  const handleUnassignIndividual = async () => {
    setAssigningIndividual(true);
    try {
      showAnswer(await unassignAppointment(appointment.id));
      toast.success("Individual unassigned successfully");
    } catch (error) {
      toast.error(fieldActionMessage(error, "Failed to unassign individual"));
    } finally {
      setAssigningIndividual(false);
    }
  };

  const hasWorker = Boolean(appointment?.assigned_user?.id);
  const status = appointment?.status;

  return {
    assigningIndividual,
    handleAssignIndividual,
    handleUnassignIndividual,
    mayAssignWorker: status !== undefined && !hasWorker && mayGiveWorker(status, false),
    mayUnassignWorker: status !== undefined && isUnassignable(status, hasWorker),
  };
}
